import { CommonModule } from '@angular/common';
import {
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Capacitor } from '@capacitor/core';
import { AlertController, IonicModule } from '@ionic/angular';
import { Subscription, firstValueFrom } from 'rxjs';
import { AdvancedModelSettingsService } from '../../../core/services/advanced-model-settings.service';
import { LiveTranslationSettingsService } from '../../../core/services/live-translation-settings.service';
import {
  ContinuousConversationService,
  ContinuousTranscript,
} from '../../../core/services/continuous-conversation.service';
import { TranslationService } from '../../../core/services/translation.service';
import { TranslatorService } from '../../../core/services/translator.service';

@Component({
  selector: 'app-continuous-conversation-modal',
  templateUrl: './continuous-conversation-modal.component.html',
  styleUrls: ['./continuous-conversation-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
})
export class ContinuousConversationModalComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() isOpen = false;
  @Input() sourceLanguage: string = 'en';
  @Input() targetLanguage: string = 'en';
  @Input() conversationTopic: string = '';
  @Output() close = new EventEmitter<void>();
  @Output() showBuyWords = new EventEmitter<void>();

  selectedSourceLanguage: string = 'en';
  selectedTargetLanguage: string = 'en';
  private previousSourceLanguage: string = '';
  private previousTargetLanguage: string = '';
  recording = false;
  transcripts: ContinuousTranscript[] = [];
  errorMessage = '';
  clickedButton: 'start' | 'stop' | 'clear' | null = null;
  statusText: string = 'Ready to transcribe';
  lectureMode = false; // Lecture mode: record every 20 seconds instead of pause-based
  useAdvancedModel = false;

  // Platform detection for Android spacing fix
  isAndroid = Capacitor.getPlatform() === 'android';
  // Detect iOS browser (Safari/Chrome on iPhone) - not just native app
  isIOSBrowser = this.detectIOSBrowser();

  private detectIOSBrowser(): boolean {
    // Check if running in iOS browser (Safari/Chrome on iPhone) - NOT native iOS app
    const platform = Capacitor.getPlatform();

    // Native iOS app should NOT be treated as iOS browser
    if (platform === 'ios') return false; // Native iOS app - use normal behavior

    // Check for iOS browser (web platform with iOS user agent)
    if (platform === 'web') {
      const userAgent =
        navigator.userAgent || navigator.vendor || (window as any).opera;
      return /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
    }

    return false; // Android, PC webapp, etc. - use normal behavior
  }

  // Audio feature
  audioEnabled = signal<boolean>(false); // Default is OFF
  private audioQueue: Array<{ audioUrl: string; text: string }> = [];
  private queueCheckInterval: any = null;
  private isPlayingAudio = false;
  private currentAudio?: HTMLAudioElement;
  private processedTranscriptIndices = new Set<number>(); // Track which transcripts have been processed
  private activeSectionId = 0; // Track active session to prevent race conditions between sections
  private processedAudioTexts = new Set<string>(); // Track processed audio texts to prevent duplicates
  private isDismissing = false; // Flag to prevent audio playback when modal is closing

  // Helper method to update status text based on recording state and translations
  private updateStatusText(): void {
    this.statusText = this.recording
      ? this.translations.transcribing || 'Transcribing...'
      : this.translations.readyToTranscribe || 'Ready to transcribe';
  }

  isGlobalAdvancedModelEnabled(): boolean {
    return this.advancedModelSettings.isGlobalAdvancedModelEnabled();
  }

  isAdvancedModelEnabled(): boolean {
    return this.advancedModelSettings.getEffectiveUseAdvancedModel(
      this.useAdvancedModel
    );
  }

  onAdvancedModelChange(enabled: boolean): void {
    if (!this.isGlobalAdvancedModelEnabled()) {
      this.useAdvancedModel = enabled;
    }
  }

  isLiveTranslationEnabled(): boolean {
    return this.liveTranslationSettings.isLiveTranslationEnabled();
  }

  // Live translation state for the current recording
  usingLive = false;
  liveNotice = '';
  liveCaption = '';

  private recordingSub?: Subscription;
  private liveCaptionSub?: Subscription;
  private transcriptsSub?: Subscription;
  private errorSub?: Subscription;
  private translationSub?: Subscription;
  translations: any = {};

  constructor(
    private readonly continuousConversation: ContinuousConversationService,
    private readonly advancedModelSettings: AdvancedModelSettingsService,
    private readonly liveTranslationSettings: LiveTranslationSettingsService,
    private readonly translationService: TranslationService,
    private readonly translatorService: TranslatorService,
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly appRef: ApplicationRef,
    private readonly alertController: AlertController
  ) {
    this.liveCaptionSub = this.continuousConversation.liveCaption.subscribe(
      (caption) => {
        this.liveCaption = caption;
        this.cdr.markForCheck();
      }
    );

    this.recordingSub = this.continuousConversation.isRecording.subscribe(
      (isRecording) => {
        console.log(
          '[ContinuousConversationModal] 📡 SUBSCRIPTION: Recording state changed from service:',
          isRecording
        );
        console.log(
          '[ContinuousConversationModal] 📊 State BEFORE subscription callback:',
          {
            recording: this.recording,
            statusText: this.statusText,
            newValue: isRecording,
          }
        );

        // Ensure this runs in Angular's zone to trigger change detection
        this.ngZone.run(() => {
          console.log(
            '[ContinuousConversationModal] 🔵 SUBSCRIPTION: Inside ngZone.run()'
          );
          const oldRecording = this.recording;
          this.recording = isRecording;
          // Update status text when recording state changes
          this.updateStatusText();

          console.log(
            '[ContinuousConversationModal] ✅ SUBSCRIPTION: Recording property changed:',
            {
              from: oldRecording,
              to: this.recording,
              statusText: this.statusText,
            }
          );

          // Mark for check to trigger change detection in next cycle
          // Using markForCheck() instead of detectChanges() to avoid "Should be run in update mode" error
          console.log(
            '[ContinuousConversationModal] 🔄 SUBSCRIPTION: Calling cdr.markForCheck()...'
          );
          this.cdr.markForCheck();
          // Force application-wide change detection
          console.log(
            '[ContinuousConversationModal] 🔄 SUBSCRIPTION: Calling appRef.tick() to force change detection...'
          );
          setTimeout(() => {
            this.appRef.tick();
            console.log(
              '[ContinuousConversationModal] ✅ SUBSCRIPTION: appRef.tick() completed'
            );
          }, 0);

          // Reset clicked button state when recording state changes
          if (this.clickedButton === 'start' && isRecording) {
            console.log(
              '[ContinuousConversationModal] ✅ SUBSCRIPTION: Recording started successfully, will reset button after delay'
            );
            // Recording started successfully, reset after a short delay
            setTimeout(() => {
              this.clickedButton = null;
              this.cdr.markForCheck();
            }, 500);
          } else if (this.clickedButton === 'stop' && !isRecording) {
            console.log(
              '[ContinuousConversationModal] ✅ SUBSCRIPTION: Recording stopped successfully, will reset button after delay'
            );
            // Recording stopped successfully, reset after a short delay
            setTimeout(() => {
              this.clickedButton = null;
              this.cdr.markForCheck();
            }, 500);
          }

          console.log(
            '[ContinuousConversationModal] 📊 SUBSCRIPTION: Final state in ngZone:',
            {
              recording: this.recording,
              statusText: this.statusText,
            }
          );
        });

        console.log(
          '[ContinuousConversationModal] 🔵 SUBSCRIPTION: Exited ngZone.run()'
        );
        console.log(
          '[ContinuousConversationModal] 📊 SUBSCRIPTION: Final state AFTER ngZone.run():',
          {
            recording: this.recording,
            statusText: this.statusText,
          }
        );
      }
    );

    this.transcriptsSub = this.continuousConversation.transcripts.subscribe(
      (transcripts) => {
        const previousLength = this.transcripts.length;
        // Reverse to show latest transcripts on top
        this.transcripts = [...transcripts].reverse();

        // Process new transcripts for audio if audio is enabled
        // (live translation plays its own translated voice instead)
        if (this.audioEnabled() && !this.usingLive) {
          console.log('🔊 Audio enabled - processing transcripts for audio:', {
            transcriptCount: transcripts.length,
            transcripts: transcripts.map((t) => ({
              chunkIndex: t.chunkIndex,
              text: t.text.substring(0, 30),
            })),
          });
          this.processNewTranscriptsForAudio(transcripts);
        } else {
          console.log('🔊 Audio disabled - skipping audio generation');
          // If audio is disabled, mark transcripts as processed so they don't
          // flood the queue if audio is toggled on later.
          // This prevents "past voices" from accumulating or playing unexpectedly.
          transcripts.forEach((t) =>
            this.processedTranscriptIndices.add(t.chunkIndex)
          );
        }

        // Trigger change detection to update the view
        this.cdr.markForCheck();
        // Reset clicked button state when transcripts change (clear completed)
        if (
          this.clickedButton === 'clear' &&
          transcripts.length === 0 &&
          previousLength > 0
        ) {
          // Clear completed successfully, reset after a short delay
          setTimeout(() => {
            this.clickedButton = null;
            this.cdr.markForCheck();
          }, 500);
        }
      }
    );

    this.errorSub = this.continuousConversation.error.subscribe((error) => {
      this.errorMessage = error || '';
      this.cdr.markForCheck();
    });

    // Load translations and subscribe to language changes
    this.translations = this.translationService.getAllTranslations();
    this.updateStatusText(); // Initialize status text with translations
    this.translationSub = this.translationService.currentLanguage$.subscribe(
      () => {
        this.translations = this.translationService.getAllTranslations();
        this.updateStatusText(); // Update status text when translations change
        this.cdr.markForCheck();
      }
    );
  }

  ngOnInit(): void {
    // Get languages from inputs - ensure they're always set
    this.selectedSourceLanguage = this.sourceLanguage || 'en';
    this.selectedTargetLanguage = this.targetLanguage || 'en';
    // Initialize previous languages for section switch detection
    this.previousSourceLanguage = this.selectedSourceLanguage;
    this.previousTargetLanguage = this.selectedTargetLanguage;
    // Ensure status text is initialized
    this.updateStatusText();

    console.log('[ContinuousConversationModal] Initialized with languages:', {
      sourceLanguage: this.selectedSourceLanguage,
      targetLanguage: this.selectedTargetLanguage,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update languages when inputs change - avoid duplicate updates
    let languageChanged = false;
    let modalOpened = false;

    if (changes['sourceLanguage']) {
      this.selectedSourceLanguage = this.sourceLanguage || 'en';
      languageChanged = true;
    }

    if (changes['targetLanguage']) {
      this.selectedTargetLanguage = this.targetLanguage || 'en';
      languageChanged = true;
    }

    // Check if modal is opening (transitioning from closed to open) or closing
    if (changes['isOpen']) {
      const wasOpen = changes['isOpen'].previousValue;
      const isNowOpen = changes['isOpen'].currentValue;
      if (!wasOpen && isNowOpen) {
        modalOpened = true;
        // Modal opening - reset dismiss flag to allow audio playback
        this.isDismissing = false;
      } else if (wasOpen && !isNowOpen) {
        // Modal is closing - set dismiss flag and reset audio state
        this.isDismissing = true;
        console.log(
          '[ContinuousConversationModal] 🔄 Modal closing - resetting audio state and clearing history'
        );
        this.activeSectionId++; // Invalidate any pending audio generation
        this.stopAllAudio();
        this.stopQueueMonitoring();
        this.processedTranscriptIndices.clear();
        this.processedAudioTexts.clear();
        // Clear conversation history when modal closes
        this.continuousConversation.clearTranscripts();
      }
    }

    // When modal opens, ensure languages are set (only log once)
    if (changes['isOpen'] && this.isOpen && languageChanged) {
      console.log(
        '[ContinuousConversationModal] Modal opened with languages:',
        {
          sourceLanguage: this.selectedSourceLanguage,
          targetLanguage: this.selectedTargetLanguage,
        }
      );
    } else if (changes['isOpen'] && this.isOpen) {
      // Modal opened but languages didn't change - just ensure they're set
      this.selectedSourceLanguage =
        this.sourceLanguage || this.selectedSourceLanguage || 'en';
      this.selectedTargetLanguage =
        this.targetLanguage || this.selectedTargetLanguage || 'en';
    }

    // Reset audio state when modal opens or languages change (switching sections)
    // This ensures audio works correctly when switching between user and partner sections
    const isSectionSwitch =
      languageChanged &&
      (this.previousSourceLanguage !== '' ||
        this.previousTargetLanguage !== '') &&
      (this.previousSourceLanguage !== this.selectedSourceLanguage ||
        this.previousTargetLanguage !== this.selectedTargetLanguage);

    if (modalOpened || isSectionSwitch) {
      this.activeSectionId++; // Invalidate any pending audio generation from previous section
      console.log(
        '[ContinuousConversationModal] 🔄 Resetting audio state for new section',
        {
          modalOpened,
          isSectionSwitch,
          previousLanguages: {
            source: this.previousSourceLanguage,
            target: this.previousTargetLanguage,
          },
          newLanguages: {
            source: this.selectedSourceLanguage,
            target: this.selectedTargetLanguage,
          },
          newSectionId: this.activeSectionId,
        }
      );

      // STOP everything and clear component state
      this.stopAllAudio();
      this.processedTranscriptIndices.clear();
      this.processedAudioTexts.clear();

      // CRITICAL: Clear the service's transcripts so we start with a clean slate.
      // This prevents old transcripts from being processed/played when switching sections.
      this.continuousConversation.clearTranscripts();

      // If audio was enabled, it will process new transcripts when they arrive (which will be fresh)
    }

    // Update previous languages for next comparison
    if (languageChanged) {
      this.previousSourceLanguage = this.selectedSourceLanguage;
      this.previousTargetLanguage = this.selectedTargetLanguage;
    }

    // Trigger change detection when languages change
    if (languageChanged) {
      this.cdr.markForCheck();
    }
  }

  async startRecording(): Promise<void> {
    console.log(
      '[ContinuousConversationModal] 🔴 START TRANSCRIBING BUTTON CLICKED'
    );
    console.log(
      '[ContinuousConversationModal] 📊 Current state BEFORE button click:',
      {
        recording: this.recording,
        statusText: this.statusText,
        clickedButton: this.clickedButton,
      }
    );

    this.errorMessage = '';
    this.clickedButton = 'start';

    // Force refresh languages from inputs before starting (in case they changed)
    if (this.sourceLanguage) {
      this.selectedSourceLanguage = this.sourceLanguage;
    }
    if (this.targetLanguage) {
      this.selectedTargetLanguage = this.targetLanguage;
    }

    // Ensure languages are set before starting - prefer input values over selected values
    const sourceLang =
      this.sourceLanguage || this.selectedSourceLanguage || 'en';
    const targetLang =
      this.targetLanguage || this.selectedTargetLanguage || 'en';

    // Get conversation topic from input and format as system prompt
    const topic = this.conversationTopic?.trim() || '';
    const systemPrompt = topic
      ? `\n\nCONVERSATION CONTEXT:\nThis conversation is about: "${topic}". The topic may be in any language - understand it regardless of the language used.\n\n🚨 CRITICAL RULES:\n- Use domain-specific terminology and context to provide more accurate transcriptions and translations\n- Translate ONLY what the user ACTUALLY SPOKE - DO NOT create stories or add information\n- DO NOT expand on the topic or generate examples/explanations\n- When translating, prioritize terms and phrases relevant to this topic, but translate ONLY the actual speech\n- The topic is for understanding context ONLY - NOT for generating new content`
      : undefined;

    console.log(
      '[ContinuousConversationModal] 🌐 Starting recording with languages:',
      {
        inputSourceLanguage: this.sourceLanguage,
        inputTargetLanguage: this.targetLanguage,
        selectedSourceLanguage: this.selectedSourceLanguage,
        selectedTargetLanguage: this.selectedTargetLanguage,
        finalSourceLang: sourceLang,
        finalTargetLang: targetLang,
        topic: topic,
        hasSystemPrompt: !!systemPrompt,
      }
    );

    if (
      sourceLang === 'en' &&
      this.sourceLanguage &&
      this.sourceLanguage !== 'en'
    ) {
      console.warn(
        '[ContinuousConversationModal] WARNING: Source language is defaulting to "en" but input is:',
        this.sourceLanguage
      );
    }

    try {
      console.log(
        '[ContinuousConversationModal] 📞 Calling service.startRecording()...'
      );

      // For iOS browser: User interaction context is active NOW (button click)
      // Clear any past/queued audios - we only want to play new audios from this recording session
      // Don't play past audios when starting a new recording session
      // Enable audio automatically so user can click "Audio On" button to start playback
      if (this.isIOSBrowser) {
        console.log(
          '🔊 [iOS Browser] User clicked "Start Transcribing" - clearing past audios and enabling audio'
        );

        // Clear past audios from queue - we don't want to play old audios
        if (this.audioQueue.length > 0) {
          console.log(
            '🔊 [iOS Browser] Clearing',
            this.audioQueue.length,
            'past audio(s) from queue'
          );
          // Revoke blob URLs to free memory
          this.audioQueue.forEach((item) => {
            if (item.audioUrl.startsWith('blob:')) {
              URL.revokeObjectURL(item.audioUrl);
            }
          });
          this.audioQueue = [];
        }

        // Stop any currently playing audio
        this.stopAllAudio();

        // Enable audio automatically (but don't play yet - user will click "Audio On" button)
        // Always enable audio for iOS browser when starting recording
        console.log('🔊 [iOS Browser] Auto-enabling audio for iOS browser');
        this.audioEnabled.set(true);
        this.cdr.markForCheck(); // Force UI update

        // Unlock audio context (may help in some cases for new audios)
        this.unlockIOSAudioContextOnStart().catch((err) => {
          console.warn('🔊 [iOS Browser] Failed to unlock audio context:', err);
        });
      }

      this.usingLive = false;
      this.liveNotice = '';
      if (this.isLiveTranslationEnabled()) {
        if (
          this.liveTranslationSettings.supportsDirectTranslation(
            sourceLang,
            targetLang
          )
        ) {
          this.usingLive = true;
        } else {
          this.liveNotice = this.translationService.formatMessage(
            'liveTranslationFallback',
            this.getLanguageName(sourceLang),
            this.getLanguageName(targetLang)
          );
        }
      }

      if (this.usingLive) {
        await this.continuousConversation.startLiveRecording(
          sourceLang,
          targetLang,
          this.audioEnabled()
        );
      } else {
        await this.continuousConversation.startRecording(
          sourceLang,
          targetLang,
          systemPrompt,
          this.lectureMode && !this.isLiveTranslationEnabled(),
          this.useAdvancedModel
        );
      }

      // For iOS browser: Ensure audio button is enabled and UI is updated
      if (this.isIOSBrowser) {
        this.audioEnabled.set(true);
        this.cdr.markForCheck();
      }

      console.log(
        '[ContinuousConversationModal] ✅ Service.startRecording() completed'
      );
      console.log(
        '[ContinuousConversationModal] 📊 State AFTER service call:',
        {
          recording: this.recording,
          statusText: this.statusText,
        }
      );

      // Force immediate update - the subscription should have fired
      // But explicitly set the recording state in Angular zone to ensure UI updates
      this.ngZone.run(() => {
        console.log('[ContinuousConversationModal] 🔵 Inside ngZone.run()');
        console.log(
          '[ContinuousConversationModal] 📊 State BEFORE setting recording=true:',
          {
            recording: this.recording,
            statusText: this.statusText,
          }
        );

        // Set recording to true immediately since service just started
        this.recording = true;
        // Update status text when recording state changes
        this.updateStatusText();

        console.log('[ContinuousConversationModal] ✅ Set recording = true');
        console.log(
          '[ContinuousConversationModal] 📊 State AFTER setting recording=true:',
          {
            recording: this.recording,
            statusText: this.statusText,
          }
        );

        // Mark for check to trigger change detection in next cycle
        // Using markForCheck() instead of detectChanges() to avoid "Should be run in update mode" error
        console.log(
          '[ContinuousConversationModal] 🔄 Calling cdr.markForCheck()...'
        );
        this.cdr.markForCheck();

        // Also trigger immediate update using setTimeout to defer detectChanges
        setTimeout(() => {
          console.log(
            '[ContinuousConversationModal] 🔄 Calling cdr.detectChanges() after setTimeout(0)...'
          );
          console.log(
            '[ContinuousConversationModal] 📊 State BEFORE detectChanges():',
            {
              recording: this.recording,
              statusText: this.statusText,
            }
          );
          this.cdr.detectChanges();
          console.log(
            '[ContinuousConversationModal] ✅ detectChanges() completed'
          );
          console.log(
            '[ContinuousConversationModal] 📊 State AFTER detectChanges():',
            {
              recording: this.recording,
              statusText: this.statusText,
            }
          );

          // Also force application-wide change detection
          console.log(
            '[ContinuousConversationModal] 🔄 Calling appRef.tick() to force application-wide change detection...'
          );
          this.appRef.tick();
          console.log(
            '[ContinuousConversationModal] ✅ appRef.tick() completed'
          );
          console.log(
            '[ContinuousConversationModal] 📊 State AFTER appRef.tick():',
            {
              recording: this.recording,
              statusText: this.statusText,
            }
          );
        }, 0);

        console.log(
          '[ContinuousConversationModal] 📋 Recording started, status should update. Final state in ngZone:',
          {
            recording: this.recording,
            statusText: this.statusText,
          }
        );
      });
      console.log('[ContinuousConversationModal] 🔵 Exited ngZone.run()');
      console.log(
        '[ContinuousConversationModal] 📊 Final state AFTER ngZone.run():',
        {
          recording: this.recording,
          statusText: this.statusText,
        }
      );
    } catch (err) {
      console.error('[ContinuousConversationModal] Start failed', err);
      this.errorMessage =
        err instanceof Error ? err.message : 'Failed to start recording';
      // Reset clicked button on error
      setTimeout(() => {
        this.clickedButton = null;
        this.cdr.markForCheck();
      }, 500);
      this.cdr.markForCheck();
    }
  }

  stopRecording(): void {
    this.clickedButton = 'stop';
    this.continuousConversation.stopRecording();

    // Clear all pending translations and audio files
    // Stop any currently playing audio
    this.stopAllAudio();

    // Clear audio queue and revoke blob URLs
    if (this.audioQueue.length > 0) {
      console.log(
        '[ContinuousConversationModal] Clearing',
        this.audioQueue.length,
        'pending audio files'
      );
      this.audioQueue.forEach((item) => {
        if (item.audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(item.audioUrl);
        }
      });
      this.audioQueue = [];
    }

    // Clear processed tracking sets
    this.processedTranscriptIndices.clear();
    this.processedAudioTexts.clear();

    this.cdr.markForCheck();
  }

  dismiss(): void {
    // Set flag to prevent any audio from playing
    this.isDismissing = true;
    this.stopRecording();
    this.stopAllAudio();
    // Clear conversation history when modal is closed
    this.clearTranscripts();
    this.close.emit();
  }

  ngOnDestroy(): void {
    // Set flag to prevent any audio from playing
    this.isDismissing = true;
    this.stopRecording();
    this.stopAllAudio();
    this.recordingSub?.unsubscribe();
    this.liveCaptionSub?.unsubscribe();
    this.transcriptsSub?.unsubscribe();
    this.errorSub?.unsubscribe();
    this.translationSub?.unsubscribe();
  }

  // Audio toggle method
  toggleAudio(): void {
    if (this.usingLive) {
      const enabled = !this.audioEnabled();
      this.audioEnabled.set(enabled);
      this.continuousConversation.setLiveAudioEnabled(enabled);
      this.cdr.markForCheck();
      return;
    }

    // For iOS browser: Special handling - if audio is already enabled and there's audio in queue,
    // clicking the button should play the audio (not disable it)
    if (
      this.isIOSBrowser &&
      this.audioEnabled() &&
      this.audioQueue.length > 0 &&
      !this.isPlayingAudio
    ) {
      console.log(
        '🔊 [iOS Browser] Audio button clicked - starting playback of',
        this.audioQueue.length,
        'queued audio(s)'
      );
      // User interaction context is active NOW (button click) - perfect time to start playback
      Promise.resolve().then(() => {
        if (
          !this.isPlayingAudio &&
          this.audioQueue.length > 0 &&
          this.audioEnabled()
        ) {
          this.playNextAudio().catch((err) => {
            console.warn('🔊 [iOS Browser] Failed to play queued audio:', err);
            // If it fails, it will be put back in queue for next retry
          });
        }
      });
      this.cdr.markForCheck();
      return; // Don't toggle - just play audio
    }

    // Normal toggle behavior for other cases
    const newValue = !this.audioEnabled();
    this.audioEnabled.set(newValue);
    console.log(
      '🔊 Continuous conversation audio toggled:',
      newValue,
      'isIOSBrowser:',
      this.isIOSBrowser
    );

    if (!newValue) {
      // If disabling audio, stop all playback
      this.stopAllAudio();
    } else {
      // For iOS browser: User interaction context is active NOW (button click)
      // This is the perfect time to start playing queued audio
      if (this.isIOSBrowser) {
        console.log(
          '🔊 [iOS Browser] Audio enabled - starting playback of queued audio'
        );
        // First, try to play any queued audio while we're in user interaction context
        if (this.audioQueue.length > 0 && !this.isPlayingAudio) {
          console.log(
            '🔊 [iOS Browser] Playing',
            this.audioQueue.length,
            'queued audio(s)'
          );
          // Use Promise.resolve().then() to execute immediately but asynchronously
          // This keeps us in the user interaction context
          Promise.resolve().then(() => {
            if (
              !this.isPlayingAudio &&
              this.audioQueue.length > 0 &&
              this.audioEnabled()
            ) {
              this.playNextAudio().catch((err) => {
                console.warn(
                  '🔊 [iOS Browser] Failed to play queued audio:',
                  err
                );
                // If it fails, it will be put back in queue for next retry
              });
            }
          });
        } else if (this.audioQueue.length === 0) {
          console.log(
            '🔊 [iOS Browser] No audio in queue yet - will play when audio is generated'
          );
        }
        // Process transcripts for new audio (if any)
        this.processTranscriptsForAudio();
      } else {
        // For non-iOS: If enabling audio, process existing transcripts
        this.processTranscriptsForAudio();
      }
    }

    this.cdr.markForCheck();
  }

  /**
   * Process transcripts for audio (used by both iOS browser and other platforms)
   */
  private processTranscriptsForAudio(): void {
    // Get current transcripts from the observable
    firstValueFrom(this.continuousConversation.transcripts)
      .then((transcripts) => {
        // Intelligent resume:
        // If the last transcript is very recent (< 5 seconds), allow it to play.
        // Otherwise, just wait for new speech.
        if (transcripts.length > 0) {
          const lastTranscript = transcripts[transcripts.length - 1];
          const timeSince =
            Date.now() - new Date(lastTranscript.timestamp).getTime();
          if (timeSince < 5000) {
            // Un-mark the last one so it gets processed
            this.processedTranscriptIndices.delete(lastTranscript.chunkIndex);
            console.log(
              `🔊 [Modal] Resuming audio: Last transcript is recent (${timeSince}ms), allowing playback.`
            );
          }
        }

        this.processNewTranscriptsForAudio(transcripts);
      })
      .catch((err) => {
        console.error('Error getting transcripts for audio:', err);
      });
  }

  /**
   * Unlock iOS browser audio context by playing a silent audio
   * This must be called in user interaction context (e.g., button click)
   */
  private async unlockIOSAudioContext(): Promise<void> {
    if (!this.isIOSBrowser) return;

    // Skip unlock attempt - iOS Safari data URLs don't work reliably
    // Instead, we'll rely on the user interaction context from the button click
    // When audio is ready, we'll try to play it directly
    console.log(
      '🔊 [iOS Browser] Skipping silent audio unlock (not reliable on iOS Safari)'
    );
    console.log(
      '🔊 [iOS Browser] Will attempt to play audio directly when ready'
    );
  }

  /**
   * Unlock iOS browser audio context when starting recording
   * This is called when user clicks "Start Transcribing" - a user interaction
   * We try to play a silent audio to unlock the context for subsequent audio
   */
  private async unlockIOSAudioContextOnStart(): Promise<void> {
    if (!this.isIOSBrowser) return;

    try {
      // Create a very short silent audio blob (1 second of silence)
      // Using Web Audio API to generate silence
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      const sampleRate = audioContext.sampleRate;
      const duration = 0.1; // 100ms of silence
      const frameCount = sampleRate * duration;
      const audioBuffer = audioContext.createBuffer(1, frameCount, sampleRate);

      // Get the audio data and create a blob
      const wav = this.audioBufferToWav(audioBuffer);
      const blob = new Blob([wav], { type: 'audio/wav' });
      const url = URL.createObjectURL(blob);

      // Try to play the silent audio to unlock the context
      const audio = new Audio(url);
      audio.volume = 0; // Make sure it's silent
      await audio.play();
      console.log('🔊 [iOS Browser] Audio context unlocked successfully');

      // Clean up
      audio.pause();
      audio.src = '';
      URL.revokeObjectURL(url);
      audioContext.close();
    } catch (error) {
      console.warn('🔊 [iOS Browser] Failed to unlock audio context:', error);
      // Continue anyway - we'll try to play audio directly when ready
    }
  }

  /**
   * Convert AudioBuffer to WAV format
   */
  private audioBufferToWav(buffer: AudioBuffer): ArrayBuffer {
    const length = buffer.length;
    const numberOfChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const arrayBuffer = new ArrayBuffer(44 + length * numberOfChannels * 2);
    const view = new DataView(arrayBuffer);
    const channels: Float32Array[] = [];
    let offset = 0;
    let pos = 0;

    // Write WAV header
    const setUint16 = (data: number) => {
      view.setUint16(pos, data, true);
      pos += 2;
    };
    const setUint32 = (data: number) => {
      view.setUint32(pos, data, true);
      pos += 4;
    };

    // RIFF identifier
    setUint32(0x46464952); // "RIFF"
    setUint32(36 + length * numberOfChannels * 2); // File length - 8
    setUint32(0x45564157); // "WAVE"

    // Format chunk
    setUint32(0x20746d66); // "fmt "
    setUint32(16); // Chunk size
    setUint16(1); // Audio format (1 = PCM)
    setUint16(numberOfChannels);
    setUint32(sampleRate);
    setUint32(sampleRate * numberOfChannels * 2); // Byte rate
    setUint16(numberOfChannels * 2); // Block align
    setUint16(16); // Bits per sample

    // Data chunk
    setUint32(0x61746164); // "data"
    setUint32(length * numberOfChannels * 2);

    // Write audio data
    for (let i = 0; i < numberOfChannels; i++) {
      channels.push(buffer.getChannelData(i));
    }

    while (pos < arrayBuffer.byteLength) {
      for (let i = 0; i < numberOfChannels; i++) {
        let sample = Math.max(-1, Math.min(1, channels[i][offset]));
        sample = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
        view.setInt16(pos, sample, true);
        pos += 2;
      }
      offset++;
    }

    return arrayBuffer;
  }

  // Process new transcripts for audio generation
  private async processNewTranscriptsForAudio(
    transcripts: ContinuousTranscript[]
  ): Promise<void> {
    // Don't process audio if modal is being dismissed
    if (this.isDismissing) {
      console.log('🔊 [Modal] Skipping audio processing - modal is dismissing');
      return;
    }

    const audioPromises: Promise<void>[] = [];
    const captureSectionId = this.activeSectionId; // Capture ID at start of processing

    for (const transcript of transcripts) {
      // Skip if already processed
      if (this.processedTranscriptIndices.has(transcript.chunkIndex)) {
        continue;
      }

      // Mark as processed
      this.processedTranscriptIndices.add(transcript.chunkIndex);

      // Generate audio for this transcript
      const audioPromise = (async () => {
        try {
          // Check if section changed before generating
          if (this.activeSectionId !== captureSectionId) {
            console.log(
              `🔊 [Modal] Section changed (captured: ${captureSectionId}, current: ${this.activeSectionId}). Skipping audio generation.`
            );
            return;
          }

          // Skip if text is empty
          if (!transcript.text || !transcript.text.trim()) {
            console.log('🔊 Skipping audio generation - empty text:', {
              chunkIndex: transcript.chunkIndex,
              text: transcript.text,
            });
            return;
          }

          console.log('🔊 Generating audio for transcript:', {
            text: transcript.text,
            targetLanguage: this.selectedTargetLanguage,
            audioEnabled: this.audioEnabled(),
          });

          // Get default voice and speed (you can make these configurable later)
          const defaultVoice = 'alloy'; // Default OpenAI voice
          const defaultSpeed = 1.0; // Normal speed

          const audioResult = await this.translatorService.tts(
            transcript.text,
            defaultSpeed,
            defaultVoice,
            this.selectedTargetLanguage
          );

          // Check again after async operation
          if (this.activeSectionId !== captureSectionId) {
            console.log(
              `🔊 [Modal] TTS finished but section changed (captured: ${captureSectionId}, current: ${this.activeSectionId}). Discarding audio.`
            );
            return;
          }

          if (audioResult && audioResult.audioUrl) {
            // Check for duplicate text before adding to queue
            const normalizedText = transcript.text.trim().toLowerCase();
            if (this.processedAudioTexts.has(normalizedText)) {
              console.log(
                '🔊 Skipping duplicate audio text:',
                transcript.text.substring(0, 50)
              );
              // Revoke the blob URL since we're not using it
              if (audioResult.audioUrl.startsWith('blob:')) {
                URL.revokeObjectURL(audioResult.audioUrl);
              }
              return;
            }

            // Mark as processed
            this.processedAudioTexts.add(normalizedText);

            // For iOS browser: Try to play immediately when audio is generated
            // This is the closest we can get to user interaction (right after TTS completes)
            // If it fails, audio will be queued and user can click "Audio On" button
            if (
              this.isIOSBrowser &&
              this.activeSectionId === captureSectionId
            ) {
              console.log(
                '🔊 [iOS Browser] Audio generated, attempting immediate playback:',
                transcript.text.substring(0, 50)
              );

              // Add to queue first
              this.audioQueue.push({
                audioUrl: audioResult.audioUrl,
                text: transcript.text,
              });
              console.log(
                '🔊 [iOS Browser] Audio added to queue. Queue length:',
                this.audioQueue.length
              );

              // Try to play immediately - this might work if we're still close to user interaction
              // If it fails, audio will remain in queue and user can click "Audio On" button
              if (!this.isPlayingAudio && this.audioQueue.length > 0) {
                console.log(
                  '🔊 [iOS Browser] Attempting immediate playback of queued audio'
                );
                // Use Promise.resolve().then() to execute immediately but asynchronously
                // This keeps us as close as possible to the user interaction context
                Promise.resolve().then(() => {
                  if (
                    !this.isPlayingAudio &&
                    this.audioQueue.length > 0 &&
                    this.audioEnabled()
                  ) {
                    this.playNextAudio().catch((err) => {
                      console.warn(
                        '🔊 [iOS Browser] Immediate playback failed, audio remains in queue. User can click "Audio On" button to play:',
                        err
                      );
                      // Audio remains in queue - ensure queue processing is active
                      this.ensureQueueProcessing();
                    });
                  }
                });
              } else if (this.isPlayingAudio && this.audioQueue.length > 0) {
                // Audio is already playing, but ensure queue will continue
                this.ensureQueueProcessing();
                // Also check immediately after a short delay to catch the latest audio
                setTimeout(() => {
                  if (
                    this.audioQueue.length > 0 &&
                    !this.isPlayingAudio &&
                    this.audioEnabled()
                  ) {
                    console.log(
                      '🔊 [iOS Browser] [Latest Audio Check] Queue has items but not playing - starting playback immediately'
                    );
                    this.playNextAudio().catch((err) => {
                      console.error(
                        '🔊 [iOS Browser] [Latest Audio Check] Error starting playback:',
                        err
                      );
                      this.isPlayingAudio = false;
                    });
                  }
                }, 100);
              }
            } else {
              // For non-iOS or if section changed: Add to queue and play normally
              this.audioQueue.push({
                audioUrl: audioResult.audioUrl,
                text: transcript.text,
              });

              console.log(
                '🔊 Audio generated and added to queue. Queue length:',
                this.audioQueue.length
              );

              // Try to start playback if not already playing (non-iOS only)
              if (!this.isPlayingAudio && this.audioQueue.length > 0) {
                // Use setTimeout to ensure this runs after the current execution context
                // This prevents multiple simultaneous calls
                // Increased delay to 300ms to allow audio focus to settle
                setTimeout(() => {
                  // Final check before playing
                  if (this.activeSectionId !== captureSectionId) {
                    return;
                  }

                  if (!this.isPlayingAudio && this.audioQueue.length > 0) {
                    this.playNextAudio().catch((err) => {
                      console.error('🔊 Error in playNextAudio:', err);
                      // Reset flag on error so queue can continue
                      this.isPlayingAudio = false;
                      // Try again if there are items in queue
                      if (this.audioQueue.length > 0) {
                        this.playNextAudio().catch(console.error);
                      }
                    });
                  }
                }, 300);
              } else if (this.isPlayingAudio && this.audioQueue.length > 0) {
                // Audio is already playing, but ensure queue will continue
                // Set up a periodic check to ensure queue processing continues
                this.ensureQueueProcessing();
                // Also check immediately after a short delay to catch the latest audio
                setTimeout(() => {
                  if (
                    this.audioQueue.length > 0 &&
                    !this.isPlayingAudio &&
                    this.audioEnabled()
                  ) {
                    console.log(
                      '🔊 [Latest Audio Check] Queue has items but not playing - starting playback immediately'
                    );
                    this.playNextAudio().catch((err) => {
                      console.error(
                        '🔊 [Latest Audio Check] Error starting playback:',
                        err
                      );
                      this.isPlayingAudio = false;
                    });
                  }
                }, 100);
              }
            }
          }
        } catch (error) {
          console.error('🔊 Error generating audio for transcript:', error);
          // Continue processing other transcripts even if one fails
        }
      })();

      audioPromises.push(audioPromise);
    }

    // Wait for all audio generation to complete
    await Promise.allSettled(audioPromises);
  }

  /**
   * Play audio immediately when generated (for iOS browser)
   * This tries to play audio as soon as it's created, without going through the queue
   * This maximizes our chance of staying within the user interaction context
   */
  private async playAudioImmediately(
    audioUrl: string,
    text: string
  ): Promise<void> {
    // Don't play audio if modal is being dismissed
    if (this.isDismissing) {
      console.log(
        '🔊 [Modal] Skipping immediate audio playback - modal is dismissing'
      );
      return;
    }

    if (!this.audioEnabled() || this.isPlayingAudio) {
      // If audio is disabled or already playing, add to queue instead
      this.audioQueue.push({ audioUrl, text });
      return;
    }

    this.isPlayingAudio = true;

    try {
      // Stop any currently playing audio
      if (this.currentAudio) {
        this.currentAudio.pause();
        this.currentAudio.currentTime = 0;
        this.currentAudio.onended = null;
        this.currentAudio.onerror = null;
      }

      // Create new audio element
      this.currentAudio = new Audio(audioUrl);
      this.currentAudio.volume = 1.0;
      this.currentAudio.preload = 'auto';

      // Set up event handlers
      this.currentAudio.onended = () => {
        console.log(
          '🔊 [Immediate] Audio ended:',
          text.substring(0, 50) + '...'
        );
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = undefined;
        this.isPlayingAudio = false;

        // Play next audio in queue if any
        if (this.audioQueue.length > 0 && this.audioEnabled()) {
          setTimeout(() => {
            this.playNextAudio().catch((err) => {
              console.error(
                '🔊 Error playing next audio after immediate playback ended:',
                err
              );
              this.isPlayingAudio = false;
            });
          }, 50);
        }
      };

      this.currentAudio.onerror = (error) => {
        console.error('🔊 [Immediate] Error playing audio:', error);
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = undefined;
        this.isPlayingAudio = false;

        // Try next audio in queue
        if (this.audioQueue.length > 0 && this.audioEnabled()) {
          this.playNextAudio().catch((err) => {
            console.error(
              '🔊 Error playing next audio after immediate playback error:',
              err
            );
            this.isPlayingAudio = false;
          });
        }
      };

      // Try to play immediately
      console.log(
        '🔊 [iOS Browser] Attempting immediate playback, readyState:',
        this.currentAudio.readyState
      );
      await this.currentAudio.play();
      console.log(
        '🔊 [iOS Browser] Immediate playback started:',
        text.substring(0, 50) + '...'
      );
    } catch (error: any) {
      // If immediate playback fails, throw error so caller can add to queue
      this.isPlayingAudio = false;
      if (this.currentAudio) {
        this.currentAudio.onended = null;
        this.currentAudio.onerror = null;
        this.currentAudio = undefined;
      }
      throw error;
    }
  }

  // Play next audio in queue
  private async playNextAudio(): Promise<void> {
    // Don't play audio if modal is being dismissed
    if (this.isDismissing) {
      console.log('🔊 [Modal] Skipping audio playback - modal is dismissing');
      this.isPlayingAudio = false;
      return;
    }

    console.log(
      '🔊 playNextAudio called. Queue size:',
      this.audioQueue.length,
      'Enabled:',
      this.audioEnabled(),
      'Playing:',
      this.isPlayingAudio
    );

    // Double-check conditions before starting
    if (this.audioQueue.length === 0 || !this.audioEnabled()) {
      this.isPlayingAudio = false;
      return;
    }

    // Prevent concurrent playback attempts
    if (this.isPlayingAudio) {
      console.log('🔊 Already playing audio, skipping...');
      return;
    }

    this.isPlayingAudio = true;
    const audioItem = this.audioQueue.shift();

    if (!audioItem) {
      this.isPlayingAudio = false;
      return;
    }

    try {
      // Stop any currently playing audio
      if (this.currentAudio) {
        this.currentAudio.pause();
        this.currentAudio.currentTime = 0;
        this.currentAudio.onended = null;
        this.currentAudio.onerror = null;
      }

      // Create new audio element
      this.currentAudio = new Audio(audioItem.audioUrl);
      this.currentAudio.volume = 1.0; // Force max volume
      this.currentAudio.preload = 'auto'; // Preload audio

      // Debug audio metadata
      this.currentAudio.onloadedmetadata = () => {
        console.log('🔊 Audio loaded. Duration:', this.currentAudio?.duration);
      };

      // For iOS browser: Try to play immediately without waiting
      // Waiting for canplay adds delay and moves us further from user interaction context
      // iOS Safari requires first play() to be in user interaction context
      // By the time canplay fires, we're too far from the interaction
      if (this.isIOSBrowser) {
        console.log(
          '🔊 [iOS Browser] Attempting immediate playback, readyState:',
          this.currentAudio.readyState
        );
        // Don't wait - try to play immediately to stay as close as possible to user interaction
      }

      // Set up event handlers BEFORE playing
      this.currentAudio.onended = () => {
        console.log(
          '🔊 Audio ended:',
          audioItem.text.substring(0, 50) + '...',
          'Queue remaining:',
          this.audioQueue.length
        );
        // Clean up
        URL.revokeObjectURL(audioItem.audioUrl);
        this.currentAudio = undefined;
        this.isPlayingAudio = false;

        // Play next audio in queue - this chains automatically
        if (this.audioQueue.length > 0 && this.audioEnabled()) {
          console.log(
            '🔊 [onended] Playing next audio in queue, remaining:',
            this.audioQueue.length
          );
          // Small delay to ensure iOS has fully released the previous audio
          setTimeout(() => {
            if (
              !this.isPlayingAudio &&
              this.audioQueue.length > 0 &&
              this.audioEnabled()
            ) {
              this.playNextAudio().catch((err) => {
                console.error('🔊 Error playing next audio after ended:', err);
                this.isPlayingAudio = false;
                // Retry if queue still has items
                if (this.audioQueue.length > 0 && this.audioEnabled()) {
                  setTimeout(
                    () => this.playNextAudio().catch(console.error),
                    100
                  );
                }
              });
            } else if (
              this.audioQueue.length > 0 &&
              !this.isPlayingAudio &&
              this.audioEnabled()
            ) {
              // Double-check: if onended didn't trigger playback, ensure queue processing is active
              console.log(
                '🔊 [onended] Queue still has items but playback not started - ensuring queue processing'
              );
              this.ensureQueueProcessing();
            }
          }, 50);
        } else {
          console.log(
            '🔊 [onended] No more audio to play, queue empty or audio disabled'
          );
          this.isPlayingAudio = false;
          this.stopQueueMonitoring();
        }
      };

      // Handle errors
      this.currentAudio.onerror = (error) => {
        console.error('🔊 Error playing audio:', error);
        URL.revokeObjectURL(audioItem.audioUrl);
        this.currentAudio = undefined;
        this.isPlayingAudio = false;

        // Try next audio in queue
        if (this.audioQueue.length > 0 && this.audioEnabled()) {
          setTimeout(() => {
            if (
              !this.isPlayingAudio &&
              this.audioQueue.length > 0 &&
              this.audioEnabled()
            ) {
              this.playNextAudio().catch((err) => {
                console.error('🔊 Error playing next audio after error:', err);
                this.isPlayingAudio = false;
                // Retry if queue still has items
                if (this.audioQueue.length > 0 && this.audioEnabled()) {
                  setTimeout(
                    () => this.playNextAudio().catch(console.error),
                    100
                  );
                }
              });
            }
          }, 100);
        } else {
          this.stopQueueMonitoring();
        }
      };

      // Play the audio - use promise-based approach for all platforms
      // For iOS browser: Try to play immediately, even if audio isn't fully loaded
      // This maximizes our chance of staying within user interaction context
      try {
        // On iOS, try to play immediately without waiting
        // If audio isn't ready, play() will queue it to play when ready
        const playPromise = this.currentAudio.play();
        console.log(
          '🔊 [iOS Browser] play() called immediately, readyState:',
          this.currentAudio.readyState
        );
        await playPromise;
        console.log(
          '🔊 Playing audio:',
          audioItem.text.substring(0, 50) + '...',
          'Queue remaining:',
          this.audioQueue.length
        );
      } catch (playError: any) {
        // On iOS browser, async-generated audio may fail on first play
        // This is expected behavior due to iOS Safari autoplay restrictions
        if (this.isIOSBrowser && playError?.name === 'NotAllowedError') {
          console.warn(
            '🔊 [iOS Browser] Audio play failed (autoplay restriction):',
            playError.message
          );
          // On iOS Safari, the first play() must be in user interaction context
          // Since TTS is async, we're outside that context by the time audio is ready
          // Put audio back at front of queue - it will retry when user clicks "Start Transcribing" again
          this.audioQueue.unshift(audioItem); // Put back at front of queue
          this.isPlayingAudio = false;
          if (this.currentAudio) {
            this.currentAudio.onended = null;
            this.currentAudio.onerror = null;
            this.currentAudio = undefined;
          }
          // Don't revoke blob URL - keep it for playback
          console.log(
            '🔊 [iOS Browser] Audio queued for retry on next user interaction. Queue size:',
            this.audioQueue.length
          );
          return; // Exit early - audio will retry when user clicks "Start Transcribing" again
        }
        throw playError; // Re-throw other errors
      }
    } catch (error) {
      console.error('🔊 Error playing audio:', error);
      URL.revokeObjectURL(audioItem.audioUrl);
      this.currentAudio = undefined;
      this.isPlayingAudio = false;

      // Try next audio in queue
      if (this.audioQueue.length > 0 && this.audioEnabled()) {
        this.playNextAudio().catch((err) => {
          console.error('🔊 Error playing next audio after catch:', err);
          this.isPlayingAudio = false;
        });
      }
    }
  }

  // Stop all audio playback
  /**
   * Ensure queue processing continues - monitors queue and starts playback if needed
   */
  private ensureQueueProcessing(): void {
    // Clear any existing interval
    if (this.queueCheckInterval) {
      clearInterval(this.queueCheckInterval);
    }

    // Set up periodic check to ensure queue is being processed
    // Use a shorter interval (300ms) to catch the latest audio more quickly
    this.queueCheckInterval = setInterval(() => {
      if (this.isDismissing) {
        this.stopQueueMonitoring();
        return;
      }

      // If queue has items but nothing is playing, start playback
      if (
        this.audioQueue.length > 0 &&
        !this.isPlayingAudio &&
        this.audioEnabled()
      ) {
        console.log(
          '🔊 [Queue Monitor] Queue has items but not playing - starting playback'
        );
        this.playNextAudio().catch((err) => {
          console.error('🔊 [Queue Monitor] Error starting playback:', err);
          this.isPlayingAudio = false;
          // Retry if queue still has items
          if (this.audioQueue.length > 0 && this.audioEnabled()) {
            setTimeout(() => this.playNextAudio().catch(console.error), 100);
          }
        });
      } else if (this.audioQueue.length === 0) {
        // Queue is empty, stop monitoring
        this.stopQueueMonitoring();
      }
    }, 300); // Check every 300ms for faster response
  }

  /**
   * Stop queue monitoring
   */
  private stopQueueMonitoring(): void {
    if (this.queueCheckInterval) {
      clearInterval(this.queueCheckInterval);
      this.queueCheckInterval = null;
    }
  }

  private stopAllAudio(): void {
    // Prevent any pending playback from starting
    this.isPlayingAudio = false;

    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      // Critical: Remove handlers so they don't trigger next playback or errors
      this.currentAudio.onended = null;
      this.currentAudio.onerror = null;
      this.currentAudio = undefined;
    }

    // Clear queue and revoke URLs
    while (this.audioQueue.length > 0) {
      const item = this.audioQueue.shift();
      if (item) {
        URL.revokeObjectURL(item.audioUrl);
      }
    }
  }

  clearTranscripts(): void {
    this.clickedButton = 'clear';
    this.stopAllAudio();
    this.processedTranscriptIndices.clear();
    this.processedAudioTexts.clear();
    this.continuousConversation.clearTranscripts();
    this.cdr.markForCheck();
    // If transcripts are already empty or clearing is immediate, reset after short delay
    if (this.transcripts.length === 0) {
      setTimeout(() => {
        this.clickedButton = null;
        this.cdr.markForCheck();
      }, 500);
    }
  }

  trackTranscript(_index: number, item: ContinuousTranscript): number {
    return item.chunkIndex;
  }

  formatTime(date: Date): string {
    return new Date(date).toLocaleTimeString();
  }

  getLanguageName(languageCode: string): string {
    const languageNames: { [key: string]: string } = {
      en: 'English',
      ko: 'Korean',
      'zh-CN': 'Mandarin',
      'zh-HK': 'Cantonese',
      zh: 'Mandarin', // Backward compatibility
      ru: 'Russian',
      ky: 'Kyrgyz',
      kk: 'Kazakh',
      tg: 'Tajik',
      tk: 'Turkmen',
      uz: 'Uzbek',
      fa: 'Farsi',
      es: 'Spanish',
    };
    return languageNames[languageCode] || languageCode || 'Unknown';
  }

  onBuyWords(): void {
    console.log('[ContinuousConversationModal] Opening buy words modal');
    // Close the continuous conversation modal first so the Buy Words modal is visible
    this.close.emit();
    // Then emit the showBuyWords event to open the Buy Words modal
    this.showBuyWords.emit();
  }

  dismissError(): void {
    console.log('[ContinuousConversationModal] Dismissing error message');
    this.errorMessage = '';
    this.continuousConversation.clearError(); // Clear error in service
    this.cdr.markForCheck();
  }

  isWordLimitError(errorMessage: string): boolean {
    if (!errorMessage) return false;

    // Check for English keywords (for backward compatibility)
    const englishKeywords = [
      'Word limit exceeded',
      'Please buy more words',
      'word limit',
    ];
    if (englishKeywords.some((keyword) => errorMessage.includes(keyword))) {
      return true;
    }

    // Check for Korean keywords
    const koreanKeywords = ['단어 한도', '단어를 사용', '단어를 구매'];
    if (koreanKeywords.some((keyword) => errorMessage.includes(keyword))) {
      return true;
    }

    // Check for Chinese keywords
    const chineseKeywords = ['单词限制', '单词', '购买更多单词'];
    if (chineseKeywords.some((keyword) => errorMessage.includes(keyword))) {
      return true;
    }

    // Check for Russian keywords
    const russianKeywords = ['Лимит слов', 'слов', 'купите больше слов'];
    if (russianKeywords.some((keyword) => errorMessage.includes(keyword))) {
      return true;
    }

    // Check for Kyrgyz keywords
    const kyrgyzKeywords = ['Сөз чеги', 'сөздү колдон', 'сөз сатып алыңыз'];
    if (kyrgyzKeywords.some((keyword) => errorMessage.includes(keyword))) {
      return true;
    }

    // If message contains word count patterns (e.g., "230 out of 224" or "230개 중 224개")
    // and mentions buying/continuing, it's likely a word limit error
    const hasWordCountPattern =
      /\d+.*\d+.*(?:out of|중|из|words|단어|слов|сөз)/i.test(errorMessage);
    const hasBuyContinuePattern =
      /(?:buy|구매|купить|сатып алыңыз|continue|계속|продолжить|улантуу)/i.test(
        errorMessage
      );

    if (hasWordCountPattern && hasBuyContinuePattern) {
      return true;
    }

    return false;
  }

  isTimeoutError(errorMessage: string): boolean {
    if (!errorMessage) return false;

    // Check for timeout keywords in multiple languages
    const timeoutKeywords = [
      'timeout',
      'took too long',
      '시간 초과', // Korean
      '超时', // Chinese
      'тайм-аут', // Russian
      'уақыты аяқталды', // Kazakh
      'вақти тамом', // Tajik
      'agotado', // Spanish
      'زمان', // Farsi
    ];

    return timeoutKeywords.some((keyword) =>
      errorMessage.toLowerCase().includes(keyword.toLowerCase())
    );
  }

  /**
   * Show lecture mode info in an alert
   */
  async showLectureModeInfo(event: Event): Promise<void> {
    event.stopPropagation(); // Prevent checkbox toggle
    const message =
      this.translations.lectureModeDescription ||
      'Record every 20 seconds for better translation context';

    const alert = await this.alertController.create({
      header: this.translations.lectureMode || 'Lecture Mode',
      message: message,
      buttons: [
        {
          text: this.translations.ok || 'OK',
          role: 'cancel',
        },
      ],
    });
    await alert.present();
  }

  async showAdvancedModelInfo(event: Event): Promise<void> {
    event.stopPropagation();
    const message =
      this.translations.advancedModelDescription ||
      'Uses gpt-5.4-mini. May be slower.';

    const alert = await this.alertController.create({
      header: this.translations.advancedModel || 'Advanced translation model',
      message,
      buttons: [
        {
          text: this.translations.ok || 'OK',
          role: 'cancel',
        },
      ],
    });
    await alert.present();
  }
}
